///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    AssignmentMethodEnum,
    AutoConnectDTO,
    AutoSetFrequenciesDTO,
    ConfigPropertiesDTO,
    DesignRingsDTO,
    EngineExceptionDTO,
    ExtendedProductdata,
    GtthDesignChartData,
    GtthLayerDTO,
    HouseDetectionDTO,
    PositionDTO
} from '../../generated/shared-types.model';
import { GtthReactiveNumber } from '../../shared/reactive-forms/input-number';
import { Validators } from '@angular/forms';
import { SuiTypeToggle } from '@siklu/sui-lib/infrastructure';
import { ExportLinkConfig } from './export-link-config.model';
import { AutoDesignPtMpModel } from './design/auto-design-ptmp.model';
import { GtthReactiveCheckbox } from '../../shared/reactive-forms/input-checkbox';
import { GtthReactiveDropdown } from '../../shared/reactive-forms/input-dropdown';

export class GtthConfigProperties implements ConfigPropertiesDTO {
    activeLayerIdx: number;
    autoSetFrequenciesPerLayer: AutoSetFrequenciesDTO[];
    autoconnectPerLayer: AutoConnectDTO[];
    autodesignP2mpPerLayer: AutoDesignPtMpModel[];
    locationNamePrefix: string;
    basic_discount: number;
    bomStepDisabled: boolean;
    configObjectVersion: string;
    defaultBuProductPerLayer: number[];
    defaultHeightPerLayer: number[];
    defaultTuProductPerLayer: number[];
    designError: EngineExceptionDTO;
    designIterationsChartData: GtthDesignChartData[];
    designRingsPerLayer: DesignRingsDTO[];
    houseDetectionConfig: HouseDetectionDTO;
    isShowPlanningMode: boolean;
    layers: GtthLayerDTO[];
    isBlockedByPole: boolean[];
    layersModeEnabled: boolean;
    nodeNamesSectorsToShow: string[];
    nodesCountPerLayer: number[];
    predefined_devices_products: Record<string, any>;
    productUnits: string;
    products_discounts: Record<string, any>;
    saved_exportLinksConfig: ExportLinkConfig;
    selectedDesignIteration: number;
    selectedProductsPerLayer: ExtendedProductdata[][];
    selectedServiceTypePerLayer: number[];
    selectedServiceYearsPerLayer: number[];
    storedPolygons: Record<string, PositionDTO[]>;
    tuRequiredRatePerLayer: number[];

    static getRequiredRateField(): GtthReactiveNumber {
        return new GtthReactiveNumber({
            key: 'requiredRate',
            disabled: true
        } as GtthReactiveNumber);
    }

    static getDefaultHeightField(): GtthReactiveNumber {
        const min = 0;

        return new GtthReactiveNumber({
            key: 'defaultHeight',
            required: true,
            step: 1,
            min,
            validationFn: Validators.compose([Validators.min(min), Validators.required])
        } as GtthReactiveNumber);
    }

    static getIsBlockedByPoleField(value: boolean, readonly: boolean): SuiTypeToggle {
        const config = new SuiTypeToggle(value);
        config.applyModifiedStateStyle = false;
        config.readonly = readonly;
        return config;
    }

    static getBlockedByPoleDegreesField(): GtthReactiveNumber {
        const min = 20;
        const max = 70;

        return new GtthReactiveNumber({
            key: 'blockedByPoleAngle',
            step: 1,
            min,
            max,
            validationFn: Validators.compose([Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getCostFacField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 20;

        return new GtthReactiveNumber({
            key: 'cost_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getCapFacField(value: number, isMhTgDesign: boolean): GtthReactiveNumber {
        const min = isMhTgDesign ? 1 : 0;
        const max = 1;

        return new GtthReactiveNumber({
            key: 'cap_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)]),
            hideInput: true
        } as GtthReactiveNumber);
    }

    static getLcentDistField(value: number, isMhTgDesign: boolean): GtthReactiveNumber {
        const min = isMhTgDesign ? 30 : 0;
        const max = 45;

        return new GtthReactiveNumber({
            key: 'lcent_dist',
            value,
            inputClass: 'form-control-sm',
            step: 1,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getNCandWinField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 255;

        return new GtthReactiveNumber({
            key: 'n_cand_win',
            value,
            inputClass: 'form-control-sm',
            step: 1,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getLimBoxesField(value: boolean): GtthReactiveCheckbox {
        return new GtthReactiveCheckbox({
            key: 'lim_boxes',
            value,
            validationFn: Validators.compose([Validators.required])
        } as GtthReactiveCheckbox);
    }

    static getPeerBsFacField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 3;

        return new GtthReactiveNumber({
            key: 'peer_bs_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getRedunFacField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 20;

        return new GtthReactiveNumber({
            key: 'redun_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getTgMaxIttrField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 10000;

        return new GtthReactiveNumber({
            key: 'tg_max_ittr',
            value,
            inputClass: 'form-control-sm',
            step: 1,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getTunnelFacField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 20;

        return new GtthReactiveNumber({
            key: 'tunnel_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getPopDistFacField(value: number, isMhTgDesign: boolean): GtthReactiveNumber {
        const min = isMhTgDesign ? 1 : 0;
        const max = 20;

        return new GtthReactiveNumber({
            key: 'pop_dist_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getAsMethodField(value: AssignmentMethodEnum): GtthReactiveDropdown {
        const asMethodOptions: any[] = [
            { value: AssignmentMethodEnum.EQUALIZE, showKey: 'Balance BU' },
            { value: AssignmentMethodEnum.WINNER_TAKES_ALL, showKey: 'Minimum BU' }
        ];
        return new GtthReactiveDropdown({
            key: 'as_method',
            value,
            options: asMethodOptions,
            valueKey: 'value',
            showKey: 'showKey',
            inputClass: 'form-control-sm',
            validationFn: Validators.required
        } as GtthReactiveDropdown);
    }

    static getMaxDevNumField(value: number): GtthReactiveNumber {
        const min = 1;
        const max = 6;

        return new GtthReactiveNumber({
            key: 'max_dev_num',
            value,
            inputClass: 'form-control-sm',
            step: 1,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getTakeRateField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 100;

        return new GtthReactiveNumber({
            key: 'take_rate',
            value,
            inputClass: 'form-control-sm',
            step: 1,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getOptBsFactorField(value: number): GtthReactiveNumber {
        const min = 1;
        const max = 10;

        return new GtthReactiveNumber({
            key: 'opt_bs_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }

    static getP2PDistFacField(value: number): GtthReactiveNumber {
        const min = 0;
        const max = 1;

        return new GtthReactiveNumber({
            key: 'p2p_dist_fac',
            value,
            inputClass: 'form-control-sm',
            step: 0.01,
            min,
            max,
            validationFn: Validators.compose([Validators.required, Validators.min(min), Validators.max(max)])
        } as GtthReactiveNumber);
    }
}
